import type { MouseEvent, ReactNode } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { Tooltip } from '@odo/shared-ui';
import { FLUID_LABEL_CLASS } from '../fluidPill';

export interface PickerTriggerButtonProps {
    glyph: ReactNode;
    label: string;
    // Border, background, text and hover classes for the selected value.
    toneClassName: string;
    chevronClassName: string;
    bold: boolean;
    onClick: (e: MouseEvent) => void;
    ariaLabel?: string;
    tooltip?: ReactNode;
    compact?: boolean;
}

// Fixed widths so the control doesn't resize as the label changes; compact still fits "Extreme",
// and is fluid: it shrinks to a narrower table column, down to icon-only.
const SIZE_CLASSES = {
    regular: { button: 'px-2.5 w-[108px] text-[12.5px]', label: 'gap-1.5', chevron: 'w-3.5 h-3.5' },
    compact: {
        button: 'px-1.5 w-[92px] max-w-full gap-0.5 text-xs',
        label: 'gap-1 h-4 whitespace-nowrap min-w-0',
        chevron: 'w-3 h-3'
    }
};

export function PickerTriggerButton({
    glyph,
    label,
    toneClassName,
    chevronClassName,
    bold,
    onClick,
    ariaLabel,
    tooltip,
    compact = false
}: PickerTriggerButtonProps) {
    const size = SIZE_CLASSES[compact ? 'compact' : 'regular'];
    // A compact label can be cut or hidden, so its tooltip and accessible name carry it.
    const tooltipContent = tooltip ?? (compact ? label : undefined);
    const accessibleName = compact && ariaLabel ? `${ariaLabel}: ${label}` : ariaLabel;
    return (
        <Tooltip content={tooltipContent} disabled={!tooltipContent}>
            <button
                type="button"
                onClick={onClick}
                aria-label={accessibleName}
                aria-haspopup="true"
                className={`border rounded-lg py-1.5 inline-flex items-center justify-between transition-colors ${size.button} ${
                    bold ? 'font-semibold' : ''
                } ${toneClassName}`}
            >
                <span className={`inline-flex items-center ${size.label}`}>
                    {glyph}
                    {compact ? <span className={FLUID_LABEL_CLASS}>{label}</span> : label}
                </span>
                <ChevronDownIcon className={`flex-shrink-0 ${size.chevron} ${chevronClassName}`} />
            </button>
        </Tooltip>
    );
}
